/**
 * GIS-formaten inlezen: GeoJSON, KML/KMZ en shapefile (.zip of losse .shp/.dbf/.prj).
 *
 * Alles wordt een GeoJSON FeatureCollection in de coördinaten van het bestand, met daarnaast
 * een gok voor het stelsel. KML is per definitie WGS84; GeoJSON en shapefiles staan in NL vaak
 * in RD (een .prj met 'Amersfoort' of een GeoJSON met crs EPSG:28992), en anders raden we aan
 * de getallen. Omrekenen naar WGS84 gebeurt in `reprojectGeoJSON`.
 *
 * Puntobjecten zijn kandidaat-boorpunten; lijnen en vlakken worden de achtergrondlaag.
 */
import { unzipSync, strFromU8 } from 'fflate';
import { guessCoordinateSystem, pointToWgs84 } from '@mv/models';

// ── GeoJSON ──────────────────────────────────────────────────────────────────

/** Stelsel uit een `crs`-lid (oude GeoJSON-spec, maar QGIS schrijft hem nog). */
function crsFromGeoJSON(gj) {
  const name = String(gj?.crs?.properties?.name || '');
  if (/28992/.test(name)) return 'RD';
  if (/25831|32631/.test(name)) return 'ETRS89_UTM31N';
  if (/4326|CRS84/.test(name)) return 'WGS84';
  return null;
}

export function parseGeoJSON(text) {
  const gj = JSON.parse(text);
  let fc;
  if (gj.type === 'FeatureCollection') fc = gj;
  else if (gj.type === 'Feature') fc = { type: 'FeatureCollection', features: [gj] };
  else if (gj.type && gj.coordinates) fc = { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: gj }] };
  else throw new Error('Geen GeoJSON');
  return { fc: { type: 'FeatureCollection', features: fc.features.filter(f => f?.geometry) }, system: crsFromGeoJSON(gj) };
}

// ── KML / KMZ ────────────────────────────────────────────────────────────────

const parseCoordList = (s) => String(s || '').trim().split(/\s+/)
  .map(t => t.split(',').map(Number)).filter(c => c.length >= 2 && c.every(Number.isFinite)).map(c => [c[0], c[1]]);

/** KML → GeoJSON. Placemarks met Point, LineString, Polygon (buitenring) en MultiGeometry. */
export function parseKml(text) {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.querySelector('parsererror')) throw new Error('KML kon niet gelezen worden');
  const features = [];
  for (const pm of doc.getElementsByTagName('Placemark')) {
    const name = pm.getElementsByTagName('name')[0]?.textContent?.trim() || null;
    const description = pm.getElementsByTagName('description')[0]?.textContent?.trim() || null;
    const props = { name, description };
    for (const d of pm.getElementsByTagName('Data')) {
      const k = d.getAttribute('name');
      if (k) props[k] = d.getElementsByTagName('value')[0]?.textContent ?? null;
    }
    for (const sd of pm.getElementsByTagName('SimpleData')) {
      const k = sd.getAttribute('name');
      if (k) props[k] = sd.textContent;
    }
    for (const pt of pm.getElementsByTagName('Point')) {
      const c = parseCoordList(pt.getElementsByTagName('coordinates')[0]?.textContent)[0];
      if (c) features.push({ type: 'Feature', properties: props, geometry: { type: 'Point', coordinates: c } });
    }
    for (const ls of pm.getElementsByTagName('LineString')) {
      const c = parseCoordList(ls.getElementsByTagName('coordinates')[0]?.textContent);
      if (c.length >= 2) features.push({ type: 'Feature', properties: props, geometry: { type: 'LineString', coordinates: c } });
    }
    for (const pg of pm.getElementsByTagName('Polygon')) {
      const outer = pg.getElementsByTagName('outerBoundaryIs')[0] || pg;
      const c = parseCoordList(outer.getElementsByTagName('coordinates')[0]?.textContent);
      if (c.length >= 3) features.push({ type: 'Feature', properties: props, geometry: { type: 'Polygon', coordinates: [c] } });
    }
  }
  return { fc: { type: 'FeatureCollection', features }, system: 'WGS84' };
}

export function parseKmz(bytes) {
  const files = unzipSync(bytes);
  const kml = Object.keys(files).find(n => n.toLowerCase().endsWith('.kml'));
  if (!kml) throw new Error('Geen .kml in het KMZ-bestand');
  return parseKml(strFromU8(files[kml]));
}

// ── Shapefile ────────────────────────────────────────────────────────────────

/** .dbf → array van records (alleen tekst/getal; genoeg voor een naamkolom). */
export function readDbf(bytes) {
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const count = v.getUint32(4, true);
  const headerLen = v.getUint16(8, true);
  const recordLen = v.getUint16(10, true);
  const fields = [];
  for (let off = 32; off < headerLen - 1 && bytes[off] !== 0x0d; off += 32) {
    let name = '';
    for (let k = 0; k < 11 && bytes[off + k]; k++) name += String.fromCharCode(bytes[off + k]);
    fields.push({ name, type: String.fromCharCode(bytes[off + 11]), len: bytes[off + 16] });
  }
  // Codepagina: in NL meestal Windows-1252 of UTF-8; probeer UTF-8 streng, anders 1252.
  const decode = (slice) => {
    try { return new TextDecoder('utf-8', { fatal: true }).decode(slice); }
    catch (_) { return new TextDecoder('windows-1252').decode(slice); }
  };
  const records = [];
  for (let r = 0; r < count; r++) {
    const start = headerLen + r * recordLen + 1; // eerste byte = verwijdermarkering
    if (start + recordLen > bytes.length + 1) break;
    const rec = {};
    let off = start;
    for (const f of fields) {
      const raw = decode(bytes.subarray(off, off + f.len)).trim();
      rec[f.name] = (f.type === 'N' || f.type === 'F') && raw !== '' && Number.isFinite(Number(raw)) ? Number(raw) : raw;
      off += f.len;
    }
    records.push(rec);
  }
  return records;
}

/** .shp → GeoJSON-geometrieën (punt, multipunt, lijn, vlak; Z/M-varianten zonder Z/M). */
export function readShp(bytes) {
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const geoms = [];
  let off = 100;
  while (off + 8 <= bytes.length) {
    const len = v.getInt32(off + 4, false) * 2;
    const p = off + 8;
    if (p + 4 > bytes.length) break;
    const type = v.getInt32(p, true);
    const base = type % 10; // 1 punt, 3 lijn, 5 vlak, 8 multipunt (+10 Z, +20 M)
    let geom = null;
    if (base === 1) {
      geom = { type: 'Point', coordinates: [v.getFloat64(p + 4, true), v.getFloat64(p + 12, true)] };
    } else if (base === 8) {
      const n = v.getInt32(p + 36, true);
      const pts = [];
      for (let k = 0; k < n; k++) pts.push([v.getFloat64(p + 40 + k * 16, true), v.getFloat64(p + 48 + k * 16, true)]);
      geom = { type: 'MultiPoint', coordinates: pts };
    } else if (base === 3 || base === 5) {
      const nParts = v.getInt32(p + 36, true);
      const nPts = v.getInt32(p + 40, true);
      const parts = [];
      for (let k = 0; k < nParts; k++) parts.push(v.getInt32(p + 44 + k * 4, true));
      const ptsOff = p + 44 + nParts * 4;
      const all = [];
      for (let k = 0; k < nPts; k++) all.push([v.getFloat64(ptsOff + k * 16, true), v.getFloat64(ptsOff + 8 + k * 16, true)]);
      const rings = parts.map((s, k) => all.slice(s, parts[k + 1] ?? nPts));
      // Vlakken ook als lijnen: voor een achtergrondtekening telt alleen de omtrek, en zo hoeven
      // we binnen- en buitenringen niet uit elkaar te halen.
      geom = { type: 'MultiLineString', coordinates: rings };
    }
    geoms.push(geom);
    off = p + len;
  }
  return geoms;
}

function systemFromPrj(prj) {
  if (!prj) return null;
  if (/Amersfoort|RD_New|28992|Stereographic/i.test(prj)) return 'RD';
  if (/UTM.*31/i.test(prj)) return 'ETRS89_UTM31N';
  if (/GEOGCS/.test(prj) && !/PROJCS/.test(prj)) return 'WGS84';
  return null;
}

/**
 * Shapefile uit losse bestanden (Map naam → bytes). Een .zip mag meerdere shapefiles bevatten;
 * die worden samengevoegd.
 */
export function parseShapefileFiles(files) {
  const names = Object.keys(files);
  const features = [];
  let system = null;
  for (const shpName of names.filter(n => n.toLowerCase().endsWith('.shp'))) {
    const stem = shpName.slice(0, -4).toLowerCase();
    const find = (ext) => names.find(n => n.toLowerCase() === stem + ext);
    const geoms = readShp(files[shpName]);
    const dbfName = find('.dbf');
    const records = dbfName ? readDbf(files[dbfName]) : [];
    const prjName = find('.prj');
    system = system || systemFromPrj(prjName ? strFromU8(files[prjName]) : null);
    geoms.forEach((g, k) => {
      if (g) features.push({ type: 'Feature', properties: { ...(records[k] || {}), _bron: shpName.split('/').pop() }, geometry: g });
    });
  }
  if (!features.length) throw new Error('Geen shapefile (.shp) gevonden');
  return { fc: { type: 'FeatureCollection', features }, system };
}

export function parseShapefileZip(bytes) {
  return parseShapefileFiles(unzipSync(bytes));
}

// ── Gemeenschappelijk ────────────────────────────────────────────────────────

/** Alle hoekpunten van een geometrie, voor het raden van het stelsel. */
export function allCoords(geom, out = []) {
  if (!geom) return out;
  const visit = (c) => { if (typeof c[0] === 'number') out.push(c); else c.forEach(visit); };
  if (geom.type === 'GeometryCollection') geom.geometries.forEach(g => allCoords(g, out));
  else visit(geom.coordinates);
  return out;
}

/** Stelsel van een FeatureCollection raden (steekproef van max. 500 hoekpunten). */
export function guessSystemForFc(fc) {
  const pts = [];
  for (const f of fc.features) { allCoords(f.geometry, pts); if (pts.length > 2000) break; }
  const step = Math.max(1, Math.floor(pts.length / 500));
  return guessCoordinateSystem(pts.filter((_, k) => k % step === 0).map(c => [c[0], c[1]]));
}

/** Hele FeatureCollection naar WGS84. Geometrieën die niet om te rekenen zijn vallen weg. */
export function reprojectGeoJSON(fc, system, swapped = false) {
  const conv = (c) => {
    const w = pointToWgs84(c[0], c[1], system, swapped);
    if (!w) throw new Error('buiten bereik');
    return [Math.round(w.x * 1e7) / 1e7, Math.round(w.y * 1e7) / 1e7];
  };
  const walk = (c) => (typeof c[0] === 'number' ? conv(c) : c.map(walk));
  const features = [];
  for (const f of fc.features) {
    try {
      const g = f.geometry;
      const geometry = g.type === 'GeometryCollection'
        ? { ...g, geometries: g.geometries.map(x => ({ ...x, coordinates: walk(x.coordinates) })) }
        : { ...g, coordinates: walk(g.coordinates) };
      features.push({ ...f, geometry });
    } catch (_) { /* object buiten het stelsel: overslaan */ }
  }
  return { type: 'FeatureCollection', features };
}

/** Puntobjecten (incl. multipunt) als kandidaat-boorpunten, met een naam uit de eigenschappen. */
export function pointFeatures(fc) {
  const out = [];
  const nameOf = (props) => {
    if (!props) return null;
    const key = Object.keys(props).find(k => /^(naam|name|nr|nummer|id|code|label|punt|boring|puntnr|title)$/i.test(k) && props[k] !== '' && props[k] != null);
    return key ? String(props[key]).trim() : null;
  };
  for (const f of fc.features) {
    const g = f.geometry;
    if (g.type === 'Point') out.push({ x: g.coordinates[0], y: g.coordinates[1], name: nameOf(f.properties), props: f.properties });
    if (g.type === 'MultiPoint') for (const c of g.coordinates) out.push({ x: c[0], y: c[1], name: nameOf(f.properties), props: f.properties });
  }
  return out;
}

/** Alleen de lijnen en vlakken (de achtergrond). */
export function nonPointFeatures(fc) {
  return { type: 'FeatureCollection', features: fc.features.filter(f => !/Point$/.test(f.geometry.type)) };
}
